import { useState, useMemo, useEffect } from 'react';
import './SchedulerComponent.css';
import CalendarDownloadButton from './CalendarDownloadButton';


// Helper function to get date components (year, month, day) in EST
// Note: This uses EST (UTC-5) year-round. May need EDT (UTC-4) adjustment for daylight saving
const getESTDateComponents = (date = new Date()) => {
  // getTime() already returns UTC milliseconds
  // EST is UTC-5, so subtract 5 hours (5 * 60 * 60 * 1000 milliseconds)
  const estOffsetMs = -5 * 60 * 60 * 1000; // EST is UTC-5 (in milliseconds)
  const utcMs = date.getTime();
  const estMs = utcMs + estOffsetMs;
  const estDate = new Date(estMs);
  return {
    year: estDate.getUTCFullYear(),
    month: estDate.getUTCMonth(),
    date: estDate.getUTCDate()
  };
};

// Helper functions for date comparisons (using EST date components)
const isDateBeforeOrToday = (dateComponents, todayComponents) => {
  return (dateComponents.year < todayComponents.year) ||
         (dateComponents.year === todayComponents.year && dateComponents.month < todayComponents.month) ||
         (dateComponents.year === todayComponents.year && dateComponents.month === todayComponents.month && dateComponents.date <= todayComponents.date);
};

const isDateAfterMaxDate = (dateComponents, maxDateComponents) => {
  return (dateComponents.year > maxDateComponents.year) ||
         (dateComponents.year === maxDateComponents.year && dateComponents.month > maxDateComponents.month) ||
         (dateComponents.year === maxDateComponents.year && dateComponents.month === maxDateComponents.month && dateComponents.date > maxDateComponents.date);
};

const isDateToday = (dateComponents, todayComponents) => {
  return dateComponents.year === todayComponents.year && 
         dateComponents.month === todayComponents.month && 
         dateComponents.date === todayComponents.date;
};

const isWeekend = (date) => {
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
};

const isDateAvailable = (date, todayComponents, maxDateComponents) => {
  const dateComponents = getESTDateComponents(date);
  return !isDateBeforeOrToday(dateComponents, todayComponents) &&
         !isDateAfterMaxDate(dateComponents, maxDateComponents) &&
         !isWeekend(date);
};

export default function SchedulerComponent( {onUpdateProgress} ) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [confirmDate, setConfirmDate] = useState('');
  const [confirmTime, setConfirmTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [reservedSlots, setReservedSlots] = useState([]);

  // API configuration for appointments
  // In development, automatically use localhost:3001
  // In production, use relative URLs (same domain via reverse proxy)
  // Override with REACT_APP_APPOINTMENT_API_URL if needed
  const APPOINTMENT_API_URL = process.env.REACT_APP_APPOINTMENT_API_URL 
    || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '');
  
  // Lambda URL for appointment submission
  const LAMBDA_URL = process.env.REACT_APP_NURSE_EDU_LAMBDA;
  
  // Authorization header for Lambda requests
  const LAMBDA_AUTHORIZATION = process.env.REACT_APP_NURSE_EDU_AUTHORIZATION;
  
  // Mailto address for Lambda requests
  const LAMBDA_MAILTO = process.env.REACT_APP_NURSE_EDU_MAILTO;

  // Available times: 8-9 AM and 7-8 PM are never available
  const availableTimes = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM', '06:00 PM'];
  
  // Get today's date components in EST
  const todayEST = useMemo(() => {
    return getESTDateComponents();
  }, []);

  // Get max date components (45 days from today in EST)
  const maxDateEST = useMemo(() => {
    const today = getESTDateComponents();
    // Create a date object from today's components, add 45 days
    const tempDate = new Date(today.year, today.month, today.date);
    tempDate.setDate(tempDate.getDate() + 45);
    return getESTDateComponents(tempDate);
  }, []);

  // Fetch reserved slots from API
  useEffect(() => {
    const fetchReservedSlots = async () => {
      try {
        // Calculate date range: today to maxDate (45 days from today)
        // Format dates as YYYY-MM-DD
        const startDate = `${todayEST.year}-${String(todayEST.month + 1).padStart(2, '0')}-${String(todayEST.date).padStart(2, '0')}`;
        const endDate = `${maxDateEST.year}-${String(maxDateEST.month + 1).padStart(2, '0')}-${String(maxDateEST.date).padStart(2, '0')}`;
        
        const requestUrl = `${APPOINTMENT_API_URL}/api/appointments/available-slots?startDate=${startDate}&endDate=${endDate}`;
        
        const response = await fetch(requestUrl);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch reserved slots: ${response.status}`);
        }
        
        const data = await response.json();
        setReservedSlots(data);
      } catch (error) {
        console.error('Error fetching reserved slots:', error);
        // Fallback to empty array on error
        setReservedSlots([]);
      }
    };
    
    fetchReservedSlots();
  }, [todayEST, maxDateEST, APPOINTMENT_API_URL]);

  // Auto-select first available date on initial load
  useEffect(() => {
    if (selectedDate === null) {
      // Start from tomorrow in EST
      const todayComponents = getESTDateComponents();
      let checkDate = new Date(todayComponents.year, todayComponents.month, todayComponents.date);
      checkDate.setDate(checkDate.getDate() + 1);
      
      // Find first available date (not weekend, within max date range)
      const maxDateComponents = maxDateEST;
      let found = false;
      let attempts = 0;
      const maxAttempts = 50; // Safety limit
      
      while (!found && attempts < maxAttempts) {
        // Check if date is available using helper function
        if (isDateAvailable(checkDate, todayComponents, maxDateComponents)) {
          // Found first available date
          const firstAvailableDate = new Date(checkDate);
          firstAvailableDate.setHours(0, 0, 0, 0);
          setSelectedDate(firstAvailableDate);
          setCurrentMonth(firstAvailableDate.getMonth());
          setCurrentYear(firstAvailableDate.getFullYear());
          found = true;
        } else {
          // Move to next day
          checkDate.setDate(checkDate.getDate() + 1);
          attempts++;
        }
      }
    }
  }, [selectedDate, todayEST, maxDateEST]);

  // Check if a slot is reserved
  const isSlotReserved = (date, time) => {
    if (!date || !time) return false;
    const dateStr = date.toISOString().split('T')[0];
    return reservedSlots.some(slot => slot.date === dateStr && slot.time === time);
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const lastDayOfPrevMonth = new Date(currentYear, currentMonth, 0);
    const startingDay = firstDay.getDay();
    const days = [];

    // Days from previous month
    for (let i = startingDay; i > 0; i--) {
      const day = lastDayOfPrevMonth.getDate() - i + 1;
      days.push({
        day,
        date: null,
        isOtherMonth: true,
        isDisabled: true
      });
    }

    // Days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dayDateLocal = new Date(currentYear, currentMonth, i);
      // Get EST date components for comparison
      const dayDateEST = getESTDateComponents(dayDateLocal);
      
      // Disable if: today or before (in EST), weekend, or more than 45 days in advance (in EST)
      const isBeforeOrToday = isDateBeforeOrToday(dayDateEST, todayEST);
      const isAfterMaxDate = isDateAfterMaxDate(dayDateEST, maxDateEST);
      const isDisabled = isBeforeOrToday || isAfterMaxDate || isWeekend(dayDateLocal);
      const isToday = isDateToday(dayDateEST, todayEST);
      const isSelected = selectedDate && dayDateLocal.getTime() === selectedDate.getTime();

      days.push({
        day: i,
        date: dayDateLocal,
        isOtherMonth: false,
        isDisabled,
        isToday,
        isSelected
      });
    }

    return days;
  }, [currentMonth, currentYear, todayEST, maxDateEST, selectedDate]);

  const currentMonthYear = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    return firstDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [currentMonth, currentYear]);

  const timeSlotsHeader = useMemo(() => {
    if (!selectedDate) return '';
    return selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' });
  }, [selectedDate]);

  // Step 1 validation
  const isStep1Complete = useMemo(() => {
    return selectedDate !== null && selectedTime !== null;
  }, [selectedDate, selectedTime]);

  // Step 2 validation
  const isStep2Complete = useMemo(() => {
    const nameValid = formData.name.trim().length >= 2;
    const phoneValid = formData.phone.replace(/\D/g, '').length >= 10;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailValid = emailRegex.test(formData.email);
    return nameValid && phoneValid && emailValid;
  }, [formData]);

  const handleDayClick = (dayData) => {
    if (dayData.isDisabled || dayData.isOtherMonth || !dayData.date) return;
    
    const date = new Date(dayData.date);
    date.setHours(0, 0, 0, 0);
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeSlotClick = (time) => {
    setSelectedTime(time);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => {
      if (prev === 0) {
        setCurrentYear(prevYear => prevYear - 1);
        return 11;
      }
      return prev - 1;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      if (prev === 11) {
        setCurrentYear(prevYear => prevYear + 1);
        return 0;
      }
      return prev + 1;
    });
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    let formatted = '';
    if (value.length > 0) { formatted = '(' + value.substring(0, 3); }
    if (value.length >= 4) { formatted += ') ' + value.substring(3, 6); }
    if (value.length >= 7) { formatted += '-' + value.substring(6, 10); }
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const nextStep = () => {
    if (currentStep < 3) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      onUpdateProgress(newStep);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      onUpdateProgress(newStep);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    const dateObj = new Date(selectedDate);
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
    
    // Extract date in YYYY-MM-DD format without timezone conversion
    // Use local date methods to avoid UTC conversion issues
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const dateISO = `${year}-${month}-${day}`;
    
    // Format appointment data
    const appointmentData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      date: formattedDate,
      time: selectedTime,
      dateISO: dateISO
    };

    console.log('appointmentData', appointmentData);

    // Save appointment to database first (for availability tracking)
    try {
      const dbResponse = await fetch(`${APPOINTMENT_API_URL}/api/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          dateISO: appointmentData.dateISO,
          time: appointmentData.time,
          name: appointmentData.name,
          email: appointmentData.email,
          phone: appointmentData.phone
        })
      });

      if (!dbResponse.ok) {
        const errorData = await dbResponse.json();
        
        // Handle conflict error (409)
        if (dbResponse.status === 409) {
          setSubmitError(errorData.error || 'This time slot is no longer available. Please select another time.');
          setIsSubmitting(false);
          return;
        }
        
        // Handle other errors
        throw new Error(errorData.error || `Database error: ${dbResponse.status}`);
      }

      // Parse the database response to get the case_id and appointment id
      const dbResponseData = await dbResponse.json();
      const caseId = dbResponseData.case_id;
      const appointmentId = dbResponseData.id;

      if (!caseId) {
        throw new Error('Failed to get case ID from database response');
      }

      if (!appointmentId) {
        throw new Error('Failed to get appointment ID from database response');
      }

      // Validate required Lambda configuration
      if (!LAMBDA_URL || !LAMBDA_AUTHORIZATION || !LAMBDA_MAILTO) {
        throw new Error('Lambda configuration is missing. Please check environment variables.');
      }

      // Database save successful, now submit to Lambda
      const lambdaPayload = {
        mailto: LAMBDA_MAILTO,
        schedulerName: appointmentData.name,
        schedulerPhone: appointmentData.phone,
        scheduledDate: appointmentData.dateISO,
        scheduledTime: appointmentData.time,
        caseId: caseId
      };

      const lambdaResponse = await fetch(LAMBDA_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': LAMBDA_AUTHORIZATION
        },
        body: JSON.stringify(lambdaPayload)
      });

      if (!lambdaResponse.ok) {
        const errorText = await lambdaResponse.text();
        throw new Error(`Lambda request failed: ${lambdaResponse.status} ${lambdaResponse.statusText}. ${errorText}`);
      }

      // Lambda notification successful, now update appointment status to 1
      const statusUpdateResponse = await fetch(`${APPOINTMENT_API_URL}/api/appointments/${appointmentId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 1 })
      });

      if (!statusUpdateResponse.ok) {
        const errorData = await statusUpdateResponse.json();
        throw new Error(errorData.error || `Failed to update appointment status: ${statusUpdateResponse.status}`);
      }

      // Success - Lambda succeeded and status updated, show success message
    } catch (error) {
      console.error('Error submitting appointment:', error);
      setSubmitError('Failed to submit appointment. Please try again or contact support.');
      setIsSubmitting(false);
      return; // Don't proceed to success step if submission fails
    }
    
    // Set confirmation data and show success message
    setConfirmDate(formattedDate);
    setConfirmTime(selectedTime);
    setCurrentStep(3);
    onUpdateProgress(3);
    setIsSubmitting(false);
  };

  // Extract date without timezone conversion
  const dateInputValue = selectedDate ? (() => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  })() : '';
  const timeInputValue = selectedTime || '';

  return (
    <div>
      <form id="contactForm" onSubmit={handleSubmit}>
        {/* Step 1: Date and Time */}
        <div className={`step ${currentStep === 1 ? 'active' : ''}`} data-step="1">
          <div className="date-time-container">
            <div className="calendar-wrapper">
              <div className="calendar-header">
                <button type="button" onClick={handlePrevMonth}>&lt;</button>
                <span>{currentMonthYear}</span>
                <button type="button" onClick={handleNextMonth}>&gt;</button>
              </div>
              <div className="calendar-grid-header">
                <div>SUN</div><div>MON</div><div>TUE</div><div>WED</div><div>THU</div><div>FRI</div><div>SAT</div>
              </div>
              <div className="calendar-grid-body">
                {calendarDays.map((dayData, index) => (
                  <div
                    key={`${dayData.date ? dayData.date.toISOString() : `prev-${index}`}`}
                    className={`calendar-day ${dayData.isOtherMonth ? 'other-month' : ''} ${dayData.isDisabled ? 'disabled' : ''} ${dayData.isToday ? 'today' : ''} ${dayData.isSelected ? 'selected' : ''}`}
                    onClick={() => handleDayClick(dayData)}
                  >
                    {dayData.day}
                  </div>
                ))}
              </div>
              <div className="timezone-info">Time zone: Eastern Time - US &amp; Canada</div>
            </div>
            <div className="time-slots-wrapper">
              <div className="time-slots-header">
                {timeSlotsHeader || 'Select a Time'}
              </div>
              <div className="time-slots-grid">
                {availableTimes.map((time) => {
                  const isSelected = selectedTime === time;
                  const isReserved = isSlotReserved(selectedDate, time);
                  // Check if selected date is today (in EST)
                  const selectedDateEST = selectedDate ? getESTDateComponents(selectedDate) : null;
                  const isToday = selectedDateEST && isDateToday(selectedDateEST, todayEST);
                  const isDisabled = !selectedDate || isReserved || isToday;
                  return (
                    <div
                      key={time}
                      className={`time-slot ${isDisabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => !isDisabled && handleTimeSlotClick(time)}
                    >
                      {time} EST
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <input type="hidden" id="contactDate" name="contactDate" value={dateInputValue} />
          <input type="hidden" id="contactTime" name="contactTime" value={timeInputValue} />
          <div className="button-group">
            <button type="button" className="btn-next" onClick={nextStep} disabled={!isStep1Complete}>
              Next
            </button>
          </div>
        </div>

        {/* Step 2: Contact Information */}
        <div className={`step ${currentStep === 2 ? 'active' : ''}`} data-step="2">
          <div className="form-group">
            <label htmlFor="name">Your full name</label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">Your phone number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              placeholder="(555) 123-4567"
              value={formData.phone}
              onChange={handlePhoneChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Your email address</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <p className="disclaimer">
              By submitting your request, you are giving Merz Pharmaceuticals, LLC and the companies working with Merz permission to use your email address and other personal information to send you information.
              Read our <a href="https://merztherapeutics.com/us/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </p>
          </div>
          {submitError && (
            <div className="form-error" style={{ color: '#e53e3e', marginBottom: '16px', padding: '12px', backgroundColor: '#fed7d7', borderRadius: '6px' }}>
              {submitError}
            </div>
          )}
          <div className="button-group">
            <button type="button" className="btn-back" onClick={prevStep} disabled={isSubmitting}>
              Back
            </button>
            <button type="submit" className="btn-next" disabled={!isStep2Complete || isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>

        {/* Success Message */}
        <div className={`step ${currentStep === 3 ? 'active' : ''}`} data-step="3">
          <div className="success-message">
            <div className="icon">✓</div>
            <h3>Thank You!</h3>
            <p>
              Your request has been submitted. A Nurse Educator will contact you on{' '}
              <strong className="text-nowrap">{confirmDate}</strong> at{' '}
              <strong className="text-nowrap">{confirmTime} EST</strong>{' '}
              <span className="text-nowrap">from 1-833-462-7452</span>.
            </p>
            {selectedDate && confirmTime && (
              <CalendarDownloadButton
                date={selectedDate}
                time={confirmTime}
                summary="Nurse Educator Appointment"
                description="Phone call appointment with a Nurse Educator from INBRIJA"
                location="1-833-462-7452"
                durationMinutes={60}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

